import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import i18n from 'i18next';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { BuscaRemota, CampoControlado, EntradaComMascara, EntradaSensivel, escrever, ler, mascaraDocumento, Rotulo, useCamposPersonalizados, validadeDocumento, validarCampos, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { useCamposDaTela } from '../../nucleo/campos-tela';
import { recuperarFoco } from '../../nucleo/foco';
import { documentoFormatado, mascaraTelefoneDigitando, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CAMINHO_EMPRESAS, corpoDosResponsaveis, invalidarVinculos, PopupVincular, responsaveisDoRegistro, useSetores, type ParceiroCrm } from './crm-comum';
import type { Ficha } from './tipos';

/**
 * Filiais junto com a matriz (cadastros 0441): as linhas do "Esta empresa tem filiais?" no formulário de empresa e os cartões Filiais
 * e Matriz da ficha. O vínculo vai por `POST /cadastros/parceiros/:id/filiais` (`cadastros.editar`); a filial nova, pelo mesmo
 * `POST /cadastros/parceiros` do formulário (`cadastros.criar`), com responsáveis e segmentos da matriz, para ficar na mesma carteira.
 */
export const MAX_LINHAS_FILIAIS = 20;
const LIMITE_NOME = 200;

export interface LinhaFilial {
  chave: string;
  modo: 'nova' | 'existente';
  nome: string;
  documento: string;
  /** A empresa escolhida (modo `existente`). */
  id: string | null;
  /** Rodada rcn-6 (SPEC-M2): os campos obrigatórios da empresa neste workspace; `tipo` e os papéis servem à condição "mostrar quando". */
  valores: Record<string, unknown>;
}
const VALORES_DA_FILIAL = { tipo: 'empresa', e_cliente: true, e_fornecedor: false, e_parceiro: false };
export const novaLinhaFilial = (modo: LinhaFilial['modo']): LinhaFilial => ({ chave: crypto.randomUUID(), modo, nome: '', documento: '', id: null, valores: VALORES_DA_FILIAL });

export type ErroDaLinha = 'nomeObrigatorio' | 'cnpjObrigatorio' | 'cnpjInvalido' | 'cnpjRepetido' | 'escolha' | 'empresaRepetida';
const soDigitos = (v: string | null | undefined) => String(v ?? '').replace(/\D/g, '');

/** A validação de antes de gravar, sem rede: nenhuma filial sai enquanto uma linha estiver errada (a matriz também não). Função pura. */
export function errosDasLinhas(linhas: LinhaFilial[], documentoDaMatriz: string | null = null, cnpjObrigatorio = false): Record<string, ErroDaLinha> {
  const erros: Record<string, ErroDaLinha> = {};
  const documentos = new Set(soDigitos(documentoDaMatriz) ? [soDigitos(documentoDaMatriz)] : []);
  const empresas = new Set<string>();
  for (const l of linhas) {
    if (l.modo === 'existente') {
      if (!l.id) erros[l.chave] = 'escolha';
      else if (empresas.has(l.id)) erros[l.chave] = 'empresaRepetida';
      else empresas.add(l.id);
      continue;
    }
    const doc = soDigitos(l.documento);
    if (!l.nome.trim()) erros[l.chave] = 'nomeObrigatorio';
    else if (cnpjObrigatorio && !l.documento.trim()) erros[l.chave] = 'cnpjObrigatorio';
    else if (l.documento.trim() && validadeDocumento(doc, 'cnpj') !== 'cnpjValido') erros[l.chave] = 'cnpjInvalido';
    else if (doc && documentos.has(doc)) erros[l.chave] = 'cnpjRepetido';
    if (doc) documentos.add(doc);
  }
  return erros;
}

export interface CamposDaFilial {
  defs: DefCampo[];
  cnpjObrigatorio: boolean;
}

/**
 * Rodada rcn-6 (SPEC-M2): o que o workspace exige de toda empresa (campos personalizados e Campos das telas), para a linha e o pop-up
 * não dependerem da recusa do servidor. O segmento herdado da matriz já preenche o obrigatório, e o campo só aparece sem ele.
 */
export function useCamposDaFilial(temSegmento: boolean): CamposDaFilial {
  const { t } = useTranslation('cadastros');
  const tela = useCamposDaTela('cadastros.parceiro');
  const setores = useSetores();
  const extras = useCamposPersonalizados('cadastros.parceiro').filter((d) => d.obrigatorio);
  const nativos: DefCampo[] = [
    { nome: 'codigo', rotulo: t('crm.filiais.codigo'), tipo: 'texto' as const },
    { nome: 'cargo', rotulo: t('crm.campos.cargo'), tipo: 'texto' as const },
    { nome: 'email', rotulo: t('crm.campos.email'), tipo: 'email' as const },
    { nome: 'telefone', rotulo: t('crm.campos.telefone'), tipo: 'telefone' as const, transformar: mascaraTelefoneDigitando },
    ...(temSegmento ? [] : [{ nome: 'setor_id', rotulo: t('crm.campos.segmento'), tipo: 'selecao' as const, opcoes: (setores.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome })) }]),
    { nome: 'observacoes', rotulo: t('crm.campos.resumo'), tipo: 'area' as const },
  ];
  return { defs: [...nativos.filter((c) => tela.obrigatorio(c.nome)).map((c) => ({ ...c, obrigatorio: true })), ...extras], cnpjObrigatorio: tela.obrigatorio('documento') };
}

/** Os erros dos campos obrigatórios por linha: as mesmas validações do `Formulario`, mais a data com texto inválido, que o valor não enxerga. */
export function errosDosCampos(linhas: LinhaFilial[], defs: DefCampo[], raiz?: ParentNode | null): Record<string, Record<string, string>> {
  const erros: Record<string, Record<string, string>> = {};
  for (const l of linhas) {
    if (l.modo !== 'nova') continue;
    const daLinha = validarCampos(defs, l.valores);
    for (const el of raiz?.querySelectorAll<HTMLElement>(`[data-chave-filial="${l.chave}"] input[data-data-invalida]`) ?? []) {
      const nome = el.closest<HTMLElement>('[data-campo-exigido]')?.dataset.campoExigido;
      if (nome) daLinha[nome] = i18n.t('comum.dataInvalida');
    }
    if (Object.keys(daLinha).length) erros[l.chave] = daLinha;
  }
  return erros;
}

/** O que a linha manda ao cadastro além do nome e do CNPJ: só os campos exigidos, com texto limpo. */
export function corpoDosCampos(l: LinhaFilial, defs: DefCampo[]): Record<string, unknown> {
  let corpo: Record<string, unknown> = {};
  for (const d of defs) {
    if (d.visivel && !d.visivel(l.valores)) continue;
    const v = ler(l.valores, d.nome);
    const limpo = typeof v === 'string' ? v.trim() : v;
    if (limpo !== undefined && limpo !== null && limpo !== '') corpo = escrever(corpo, d.nome, limpo);
  }
  return corpo;
}

/** O que a filial nova herda da matriz, para aparecer na mesma carteira de quem cadastrou. */
export function herancaDaMatriz(responsaveis: string[], segmentos: string[]) {
  const r = corpoDosResponsaveis(responsaveis);
  return { principal: { ...r.principal, setor_id: segmentos[0] ?? null }, adicionais: { setor_ids: segmentos.slice(1), ...r.adicionais } };
}
export type HerancaFilial = ReturnType<typeof herancaDaMatriz>;

export interface FalhaDaFilial {
  nome: string;
  etapa: 'criar' | 'vincular' | 'responsaveis';
  motivo: string;
}

/** Grava uma linha e devolve o que faltou (ou null). Nada se perde em silêncio: a filial criada sem vínculo é dita como tal. */
async function gravarLinha(matrizId: string, l: LinhaFilial, nome: string, h: HerancaFilial, defs: DefCampo[]): Promise<FalhaDaFilial | null> {
  let id = l.id;
  if (l.modo === 'nova') {
    try {
      const documento = soDigitos(l.documento);
      id = (await post<{ id: string }>('/cadastros/parceiros', { tipo: 'empresa', e_cliente: true, situacao_comercial: 'lead', origem: 'crm', nome: l.nome.trim(), ...(documento ? { documento } : {}), ...h.principal, ...corpoDosCampos(l, defs) })).id;
    } catch (e) {
      return { nome, etapa: 'criar', motivo: mensagemDeErro(e) };
    }
  }
  try {
    await post(`/cadastros/parceiros/${matrizId}/filiais`, { filial_id: id });
  } catch (e) {
    return { nome, etapa: 'vincular', motivo: mensagemDeErro(e) };
  }
  if (l.modo === 'nova' && (h.adicionais.setor_ids.length || h.adicionais.responsavel_ids.length || h.adicionais.responsavel_todos)) {
    try {
      await api('PUT', `/cadastros/parceiros/${id}/adicionais`, h.adicionais);
    } catch (e) {
      return { nome, etapa: 'responsaveis', motivo: mensagemDeErro(e) };
    }
  }
  return null;
}

/** Uma de cada vez: a mesma ordem da tela, e uma falha não impede as outras. Devolve o que faltou. */
export async function gravarFiliais(matrizId: string, linhas: { linha: LinhaFilial; nome: string }[], h: HerancaFilial, defs: DefCampo[] = []): Promise<FalhaDaFilial[]> {
  const falhas: FalhaDaFilial[] = [];
  for (const { linha, nome } of linhas) {
    const f = await gravarLinha(matrizId, linha, nome, h, defs);
    if (f) falhas.push(f);
  }
  return falhas;
}

/** Os campos de uma filial nova: nome obrigatório e CNPJ opcional, com a máscara e a validação do formulário da empresa. */
function CamposDaFilialNova({ l, aoMudar, erro, base, campos, errosCampos }: { l: LinhaFilial; aoMudar: (d: Partial<LinhaFilial>) => void; erro?: ErroDaLinha; base: string; campos: CamposDaFilial; errosCampos?: Record<string, string> }) {
  const { t } = useTranslation('cadastros');
  const erroNome = erro === 'nomeObrigatorio' ? t('crm.filiais.erros.nomeObrigatorio') : undefined;
  const erroCnpj = erro === 'cnpjObrigatorio' || erro === 'cnpjInvalido' || erro === 'cnpjRepetido' ? t(`crm.filiais.erros.${erro}`) : undefined;
  return (
    <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2" data-chave-filial={l.chave}>
      <Rotulo id={`${base}-nome`} rotulo={t('crm.filiais.nome')} obrigatorio erro={erroNome}>
        <EntradaSensivel id={`${base}-nome`} data-campo-filial="nome" mascara="nome" rotulo={t('crm.filiais.nome')} maxLength={LIMITE_NOME} aria-invalid={!!erroNome} aria-describedby={erroNome ? `${base}-nome-erro` : undefined} value={l.nome} onChange={(e) => aoMudar({ nome: e.target.value })} className="h-11 w-full rounded-lg border border-borda-campo bg-campo px-3 text-sm text-tinta-1 md:h-10" />
      </Rotulo>
      <Rotulo id={`${base}-cnpj`} rotulo={t('crm.filiais.cnpj')} obrigatorio={campos.cnpjObrigatorio} erro={erroCnpj}>
        <EntradaComMascara id={`${base}-cnpj`} data-campo-filial="cnpj" mascara="documento" rotulo={t('crm.filiais.cnpj')} inputMode="numeric" aria-invalid={!!erroCnpj} aria-describedby={erroCnpj ? `${base}-cnpj-erro` : undefined} formato={(v: string) => mascaraDocumento(v, 'cnpj')} valor={l.documento} aoMudarTexto={(documento) => aoMudar({ documento })} className="h-11 w-full rounded-lg border border-borda-campo bg-campo px-3 text-sm text-tinta-1 md:h-10" />
      </Rotulo>
      {campos.defs
        .filter((d) => !d.visivel || d.visivel(l.valores))
        .map((d) => (
          <div key={d.nome} className="min-w-0" data-campo-exigido={d.nome}>
            <CampoControlado def={d} valor={ler(l.valores, d.nome)} erro={errosCampos?.[d.nome]} aoMudar={(v) => aoMudar({ valores: escrever(l.valores, d.nome, d.transformar && typeof v === 'string' ? d.transformar(v) : v) })} />
          </div>
        ))}
    </div>
  );
}

/** "Esta empresa tem filiais?" e as linhas: cadastrar cada filial ou escolher uma empresa já cadastrada. */
export function BlocoFiliais({ linhas, aoMudar, erros, campos, errosCampos }: { linhas: LinhaFilial[]; aoMudar: (l: LinhaFilial[]) => void; erros: Record<string, ErroDaLinha>; campos: CamposDaFilial; errosCampos: Record<string, Record<string, string>> }) {
  const { t } = useTranslation('cadastros');
  const id = useId();
  const raiz = useRef<HTMLDivElement>(null);
  // Rodada rcn-6 (VIS-B3): acrescentar leva o foco à linha nova; na 20ª, os botões somem, e o foco não salta ao início do formulário.
  const focarNova = useRef(false);
  useEffect(() => {
    if (!focarNova.current) return;
    focarNova.current = false;
    raiz.current?.querySelector<HTMLElement>('[data-linha-filial]:last-of-type input:not([type="checkbox"]), [data-linha-filial]:last-of-type [data-marcador]')?.focus();
  }, [linhas.length]);
  const acrescentar = (modo: LinhaFilial['modo']) => {
    focarNova.current = true;
    aoMudar([...linhas, novaLinhaFilial(modo)]);
  };
  const mudar = (chave: string, d: Partial<LinhaFilial>) => aoMudar(linhas.map((l) => (l.chave === chave ? { ...l, ...d } : l)));
  return (
    <div ref={raiz} className="flex min-w-0 flex-col gap-3" data-filiais-formulario="">
      <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium text-tinta-2 md:min-h-9">
        <input type="checkbox" data-tem-filiais="" checked={linhas.length > 0} onChange={(e) => aoMudar(e.target.checked ? [novaLinhaFilial('nova')] : [])} />
        {t('crm.filiais.tem')}
      </label>
      {linhas.map((l, i) => {
        const base = `${id}-${l.chave}`;
        const erroBusca = erros[l.chave] === 'escolha' || erros[l.chave] === 'empresaRepetida' ? t(`crm.filiais.erros.${erros[l.chave]}`) : undefined;
        return (
          <div key={l.chave} className="flex min-w-0 items-start gap-2 rounded-lg border border-borda p-2" data-linha-filial={l.modo}>
            {l.modo === 'nova' ? (
              <CamposDaFilialNova l={l} base={base} erro={erros[l.chave]} campos={campos} errosCampos={errosCampos[l.chave]} aoMudar={(d) => mudar(l.chave, d)} />
            ) : (
              <div className="min-w-0 flex-1">
                <Rotulo id={`${base}-busca`} rotulo={t('crm.filiais.empresa')} obrigatorio erro={erroBusca}>
                  <BuscaRemota id={`${base}-busca`} aria-invalid={!!erroBusca} aria-describedby={erroBusca ? `${base}-busca-erro` : undefined} def={{ caminho: '/cadastros/parceiros', params: { papel: 'empresa', elegivel_filial: 'true' }, rotulo: (x) => String(x.nome), pessoa: true }} valor={l.id} aoMudar={(v, item) => mudar(l.chave, { id: v, nome: item ? String(item.nome) : '' })} />
                </Rotulo>
              </div>
            )}
            <Botao variante="fantasma" tamanho="sm" aria-label={t('crm.filiais.tirarLinha', { n: i + 1 })} title={t('crm.filiais.tirarLinha', { n: i + 1 })} icone={<X className="size-4" />} onClick={() => aoMudar(linhas.filter((x) => x.chave !== l.chave))} />
          </div>
        );
      })}
      {linhas.length > 0 && linhas.length < MAX_LINHAS_FILIAIS && (
        <div className="flex flex-wrap gap-2">
          <Botao variante="fantasma" tamanho="sm" icone={<Plus className="size-4" />} onClick={() => acrescentar('nova')} data-mais-filial="nova">
            {t('crm.filiais.cadastrarOutra')}
          </Botao>
          <Botao variante="fantasma" tamanho="sm" icone={<Plus className="size-4" />} onClick={() => acrescentar('existente')} data-mais-filial="existente">
            {t('crm.filiais.escolherCadastrada')}
          </Botao>
        </div>
      )}
    </div>
  );
}

/** Pop-up central do "Cadastrar filial" da ficha da matriz. */
function CadastrarFilial({ matrizId, heranca, aoFechar }: { matrizId: string; heranca: HerancaFilial; aoFechar: () => void }) {
  const campos = useCamposDaFilial(!!heranca.principal.setor_id);
  const raiz = useRef<HTMLDivElement>(null);
  const [errosCampos, setErrosCampos] = useState<Record<string, string> | undefined>();
  const emCurso = useRef(false);
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const [linha, setLinha] = useState(() => novaLinhaFilial('nova'));
  const [erro, setErro] = useState<ErroDaLinha | undefined>();
  const [geral, setGeral] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const gravar = async () => {
    // Rodada rcn-6 (ROB-M1): ocupado até o fim da releitura, e a trava pega o segundo clique antes do novo desenho.
    if (emCurso.current) return;
    const e = errosDasLinhas([linha], null, campos.cnpjObrigatorio)[linha.chave];
    const doCampo = errosDosCampos([linha], campos.defs, raiz.current)[linha.chave];
    setErro(e);
    setErrosCampos(doCampo);
    if (e || doCampo) {
      window.setTimeout(() => raiz.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 0);
      return;
    }
    emCurso.current = true;
    setOcupado(true);
    setGeral(null);
    try {
      const [falha] = await gravarFiliais(matrizId, [{ linha, nome: nomePessoa(linha.nome.trim()) }], heranca, campos.defs);
      // Sem cadastro: o formulário fica aberto com o motivo. Já cadastrada: fechar e avisar o que faltou (repetir duplicaria a empresa).
      if (falha?.etapa === 'criar') return setGeral(falha.motivo);
      await invalidarVinculos(qc);
      if (falha) toast.erro(textoDaFalha(t, falha));
      else toast.sucesso(t('crm.filiais.cadastrada'));
      aoFechar();
    } finally {
      emCurso.current = false;
      setOcupado(false);
    }
  };
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.filiais.cadastrar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={ocupado} onClick={() => void gravar()} data-gravar-filial="">
            {t('crm.filiais.cadastrar')}
          </Botao>
        </>
      }
    >
      <div ref={raiz} className="flex flex-col gap-2">
        <CamposDaFilialNova l={linha} base={id} erro={erro} campos={campos} errosCampos={errosCampos} aoMudar={(d) => setLinha((x) => ({ ...x, ...d }))} />
        {geral && <p role="alert" className="text-sm text-perigo">{geral}</p>}
      </div>
    </Modal>
  );
}

type Traducao = (chave: string, opcoes?: Record<string, unknown>) => string;
export const textoDaFalha = (t: Traducao, f: FalhaDaFilial) => t(`crm.filiais.falha.${f.etapa}`, { nome: f.nome, motivo: f.motivo });

/** Rodada rcn-6 (ROB-B2): as falhas por etapa e motivo, para muitas linhas caberem na tela. Função pura. */
export function falhasPorMotivo(falhas: FalhaDaFilial[]): { etapa: FalhaDaFilial['etapa']; motivo: string; nomes: string[] }[] {
  const grupos = new Map<string, { etapa: FalhaDaFilial['etapa']; motivo: string; nomes: string[] }>();
  for (const f of falhas) {
    const motivo = f.motivo.replace(/\.+$/, '');
    const k = `${f.etapa}:${motivo}`;
    const g = grupos.get(k);
    if (g) g.nomes.push(f.nome);
    else grupos.set(k, { etapa: f.etapa, motivo, nomes: [f.nome] });
  }
  return [...grupos.values()];
}

/** O que faltou das filiais, depois de a matriz gravar: fica na tela até a pessoa fechar, com rolagem, em vez de um aviso que some. */
export function ResultadoDasFiliais({ falhas, total, aoFechar }: { falhas: FalhaDaFilial[]; total: number; aoFechar: () => void }): ReactNode {
  const { t } = useTranslation('cadastros');
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.filiais.resultadoTitulo')}
      rodape={
        <Botao variante="primario" onClick={aoFechar} data-fechar-resultado-filiais="">
          {t('crm.filiais.entendi')}
        </Botao>
      }
    >
      <div className="flex flex-col gap-3 text-sm" data-resultado-filiais="">
        <p>{t('crm.filiais.parcial', { feitas: total - falhas.length, total })}</p>
        <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
          {falhasPorMotivo(falhas).map((g, i) => (
            <li key={i} className="rounded-lg border border-borda p-2">
              <p className="font-medium text-tinta-1">{t(`crm.filiais.grupo.${g.etapa}`, { motivo: g.motivo })}</p>
              <p className="text-tinta-2">{g.nomes.join(', ')}</p>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

type EmpresaDaFicha = Ficha & ParceiroCrm;

/** O cartão Filiais da matriz: cadastrar, vincular existente, desvincular e abrir. Filial não tem filiais, então não o mostra. */
export function CartaoFiliais({ e }: { e: EmpresaDaFicha }) {
  const { t } = useTranslation('cadastros');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [adicionar, setAdicionar] = useState(false);
  const [cadastrar, setCadastrar] = useState(false);
  const [tirar, setTirar] = useState<{ id: string; nome: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Rodada rcn-6 (ROB-B3): a ficha corta em 200 (a mescla pode passar do teto); "Ver todas" traz o resto da lista.
  const [todas, setTodas] = useState<{ id: string; nome: string; documento: string | null }[] | null>(null);
  const [carregandoTodas, setCarregandoTodas] = useState(false);
  const podeEditar = s.pode('cadastros.editar');
  if (e.matriz_id) return null;
  const lista = todas ?? e.filiais ?? [];
  const total = e.filiais_total ?? lista.length;
  const verTodas = async () => {
    setCarregandoTodas(true);
    try {
      setTodas((await getTodos<{ id: string; nome: string; documento: string | null }>('/cadastros/parceiros', { papel: 'empresa', grupo_de: e.id })).itens);
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    } finally {
      setCarregandoTodas(false);
    }
  };
  const confirmarTirar = async () => {
    if (!tirar) return;
    setOcupado(true);
    const posicao = lista.findIndex((f) => f.id === tirar.id);
    try {
      await del(`/cadastros/parceiros/${e.id}/filiais/${tirar.id}`);
      setTirar(null);
      setTodas((x) => x?.filter((f) => f.id !== tirar.id) ?? null);
      await invalidarVinculos(qc);
      toast.sucesso(t('crm.filiais.desvinculada'));
      // Rodada rcn-6 (VIS-B2): o botão saiu com a linha; o foco vai à linha que ficou no lugar, senão a Adicionar filial.
      recuperarFoco({ seletor: '[data-desvincular-filial]', i: posicao, reservas: ['[data-abrir-adicionar-filial]'] });
    } catch (err) {
      setTirar(null);
      toast.erro(mensagemDeErro(err));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <>
      <Cartao
        titulo={total ? `${t('crm.filiais.titulo')} (${total})` : t('crm.filiais.titulo')}
        acao={
          podeEditar &&
          !e.arquivado &&
          !e.filiais_no_teto && (
            <Botao tamanho="sm" variante="fantasma" icone={<Plus className="size-4" />} onClick={() => setAdicionar(true)} data-abrir-adicionar-filial="">
              {t('crm.filiais.adicionar')}
            </Botao>
          )
        }
      >
        {e.filiais_no_teto && podeEditar && !e.arquivado && <p className="mb-2 text-xs text-tinta-3" data-filiais-no-teto="">{t('crm.filiais.noTeto')}</p>}
        {lista.length ? (
          <ul className="flex flex-col" data-filiais-empresa="">
            {lista.map((f) => (
              <li key={f.id} className="flex min-h-11 items-center gap-2 border-b border-borda px-2 text-sm last:border-0" data-filial={f.id}>
                <span className="flex min-w-0 flex-1 flex-col">
                  <Link to={`${CAMINHO_EMPRESAS}/${f.id}`} className="truncate font-semibold text-azul hover:underline" title={nomePessoa(f.nome)}>
                    {nomePessoa(f.nome)}
                  </Link>
                  {f.documento && <span className="numero truncate text-xs text-tinta-3">{documentoFormatado(f.documento)}</span>}
                </span>
                {podeEditar && (
                  <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.filiais.desvincularDe', { nome: nomePessoa(f.nome) })} title={t('crm.filiais.desvincular')} icone={<X className="size-4" />} onClick={() => setTirar(f)} data-desvincular-filial={f.id} />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-tinta-3">{t('crm.filiais.vazio')}</p>
        )}
        {total > lista.length && (
          <div className="mt-2 flex flex-col items-start gap-1" data-filiais-cortadas="">
            <p className="text-xs text-tinta-3">{t('crm.filiais.cortadas', { mostradas: lista.length, total })}</p>
            <Botao tamanho="sm" variante="fantasma" carregando={carregandoTodas} onClick={() => void verTodas()} data-ver-todas-filiais="">
              {t('crm.filiais.verTodas')}
            </Botao>
          </div>
        )}
      </Cartao>
      <PopupVincular
        aberto={adicionar}
        aoFechar={() => setAdicionar(false)}
        titulo={t('crm.filiais.adicionar')}
        rotulo={t('crm.filiais.empresa')}
        acao={t('crm.aj.vincular')}
        busca={{ params: { papel: 'empresa', elegivel_filial: 'true', nao_e: e.id } }}
        cadastrar={t('crm.filiais.cadastrar')}
        aoCadastrar={
          s.pode('cadastros.criar')
            ? () => {
                setAdicionar(false);
                setCadastrar(true);
              }
            : undefined
        }
        marca="filial"
        sucesso={t('crm.filiais.vinculada')}
        aoVincular={(filial_id) => post(`/cadastros/parceiros/${e.id}/filiais`, { filial_id })}
      />
      {cadastrar && <CadastrarFilial matrizId={e.id} heranca={herancaDaMatriz(responsaveisDoRegistro(e), [...(e.setor_id ? [e.setor_id] : []), ...(e.setores_adicionais ?? []).map((x) => x.id)])} aoFechar={() => setCadastrar(false)} />}
      <Confirmar
        aberto={!!tirar}
        aoFechar={() => setTirar(null)}
        titulo={t('crm.filiais.desvincular')}
        texto={t('crm.filiais.desvincularTexto', { nome: nomePessoa(tirar?.nome) })}
        carregando={ocupado}
        aoConfirmar={() => void confirmarTirar()}
      />
    </>
  );
}

/** O cartão Matriz da filial: abrir e desvincular. A matriz arquivada segue no cartão, marcada como inativa, e o vínculo não se desfaz sozinho. */
export function CartaoMatriz({ e }: { e: EmpresaDaFicha }) {
  const { t } = useTranslation('cadastros');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [tirar, setTirar] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const m = e.matriz;
  if (!m) return null;
  const confirmar = async () => {
    setOcupado(true);
    try {
      await del(`/cadastros/parceiros/${m.id}/filiais/${e.id}`);
      setTirar(false);
      await invalidarVinculos(qc);
      toast.sucesso(t('crm.filiais.desvinculada'));
      // Rodada rcn-6 (VIS-B2): o cartão Matriz some; o foco vai à ação do cartão Filiais, que aparece no lugar.
      recuperarFoco({ reservas: ['[data-abrir-adicionar-filial]'] });
    } catch (err) {
      setTirar(false);
      toast.erro(mensagemDeErro(err));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <>
      <Cartao titulo={t('crm.filiais.matriz')}>
        <div className="flex min-h-11 items-center gap-2 px-2 text-sm" data-matriz-empresa={m.id}>
          <Link to={`${CAMINHO_EMPRESAS}/${m.id}`} className="min-w-0 flex-1 truncate font-semibold text-azul hover:underline" title={nomePessoa(m.nome)}>
            {nomePessoa(m.nome)}
          </Link>
          {m.arquivada && <Selo cor="dourado">{t('crm.inativar.selo')}</Selo>}
          {s.pode('cadastros.editar') && (
            <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.filiais.desvincularDaMatriz', { nome: nomePessoa(m.nome) })} title={t('crm.filiais.desvincular')} icone={<X className="size-4" />} onClick={() => setTirar(true)} data-desvincular-matriz="" />
          )}
        </div>
      </Cartao>
      <Confirmar aberto={tirar} aoFechar={() => setTirar(false)} titulo={t('crm.filiais.desvincular')} texto={t('crm.filiais.desvincularMatrizTexto', { nome: nomePessoa(m.nome) })} carregando={ocupado} aoConfirmar={() => void confirmar()} />
    </>
  );
}
